import React from "react";
import { Box, Chip, Stack } from "@mui/material";

/**
 * CategoryChipsBar
 *
 * Props:
 * @param {number} totalCount - 전체 항목 수 (모두보기 표시용) [Required]
 * @param {Array} categories - { key: string, label: string, count: number } 배열 [Required]
 * @param {string} selectedCategory - 현재 선택된 카테고리 키 [Optional]
 * @param {function} onCategoryClick - 카테고리 클릭 핸들러 (key|'all') [Required]
 * @param {boolean} isMobile - 모바일 여부 [Optional, 기본값: false]
 * @param {object} sx - 최상위 컨테이너에 추가할 sx [Optional]
 *
 * Example usage:
 * <CategoryChipsBar
 *   totalCount={120}
 *   categories={[{ key: 'tech', label: '기술', count: 10 }]}
 *   selectedCategory={null}
 *   onCategoryClick={(k) => setSelectedCategory(k)}
 *   isMobile={false}
 * />
 */
export const CategoryChipsBar = ({
	totalCount,
	categories,
	selectedCategory,
	onCategoryClick,
	isMobile = false,
	sx = {},
}) => {
	return (
		<Box
			sx={{
				backgroundColor: "background.default",
				py: isMobile ? 1 : 2,
				px: 0,
				width: "100%",
				maxWidth: "100%",
				marginBottom: 0,
				boxSizing: "border-box",
				overflowX: "hidden",
				contain: "inline-size",
				minWidth: 0,
				display: "block",
				flex: "0 0 auto",
				...sx,
			}}
		>
			<Box
				sx={{
					width: "100%",
					maxWidth: "100%",
					overflowX: "auto",
					overflowY: "hidden",
					WebkitOverflowScrolling: "touch",
					overscrollBehaviorX: "contain",
					scrollbarWidth: "none",
					msOverflowStyle: "none",
					"&::-webkit-scrollbar": {
						display: "none",
					},
					px: 0,
					py: 0,
					boxSizing: "border-box",
					minWidth: 0,
					display: "block",
				}}
			>
				<Box
					sx={{
						display: "inline-block",
						width: "max-content",
						whiteSpace: "nowrap",
						padding: 0,
						boxSizing: "border-box",
					}}
				>
					<Stack
						direction="row"
						sx={{
							flexWrap: "nowrap",
							gap: isMobile ? 1 : 1.5, // CSS gap 사용: 음수 margin 방지
							m: 0,
						}}
						useFlexGap
					>
						<Chip
							label={`모두보기 (${totalCount})`}
						onClick={() => onCategoryClick && onCategoryClick("all")}
						color={selectedCategory === "all" ? "primary" : "default"}
						variant={selectedCategory === "all" ? "filled" : "outlined"}
							sx={{
								flexShrink: 0,
								height: isMobile ? 32 : 52,
								fontSize: isMobile ? "0.875rem" : "1.125rem",
								borderRadius: "999px",
								"& .MuiChip-label": {
									px: isMobile ? 1.5 : 3,
									fontWeight: isMobile ? 700 : 800,
								},
							}}
						/>
						{categories.map((cat) => {
							const isSelected = selectedCategory === cat.key;
							return (
								<Chip
									key={cat.key}
									label={`${cat.label} (${cat.count})`}
									onClick={() => onCategoryClick && onCategoryClick(cat.key)}
									color={isSelected ? "primary" : "default"}
									variant={isSelected ? "filled" : "outlined"}
									sx={{
										flexShrink: 0,
										height: isMobile ? 32 : 52,
										fontSize: isMobile ? "0.875rem" : "1.125rem",
										borderRadius: "999px",
										"& .MuiChip-label": {
											px: isMobile ? 1.5 : 3,
											fontWeight: isMobile ? 700 : 800,
										},
									}}
								/>
							);
						})}
					</Stack>
				</Box>
			</Box>
		</Box>
	);
};

export default CategoryChipsBar;
